import * as React from "react";
import {cx}       from "emotion";

import "./paginator.scss";

import {Link,
        makeChain}     from "@Common/react/cst2";
import {Types}         from "@Common/types";
import {Select}        from "@Common/ui-components/heavy/common/design2/select/select";


export const Paginator = ({
    numItems,
    pageNumber,
    itemsPerPage,
    onPaginateNumResults,
    onPaginatePageNumber,
}:{
    numItems              : number,
    pageNumber            : number,
    itemsPerPage          : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    onPaginateNumResults  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
    onPaginatePageNumber  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginatePageNumber,
}) => (

    makeChain<JSX.Element>()

    // build the list of items per page that can be selected
    .then("itemsPerPageOptions", (_, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.numResultsPerPage[]>) => next([10, 20, 50, 100]))

    // build the list of page numbers to show in the page selection navigator
    .then("pageOptions", (_, next:Link<JSX.Element, number[]>) => {

        const maxDisplayPages = 4;
        let numPages = Math.ceil(numItems / itemsPerPage);
        let options = [];

        // show all pages if within the max number of pages to display
        if (numPages <= maxDisplayPages) {
            for (let i = 1; i <= numPages; i++) options.push(i);
        }

        /* show only a subset of pages if more pages than the max allows - format is atypical - examples as what would
            be shown for each current page are:
            Page 1 of 8: |1|2|-|8|
            Page 2 of 8: |2|3|-|8|
            Page 3 of 8: |3|4|-|8|
            Page 4 of 8: |4|5|-|8|
            Page 5 of 8: |5|6|7|8|
            Page 6 of 8: |5|6|7|8|
            Page 7 of 8: |1|-|7|8|
            Page 8 of 8: |1|-|7|8|
        */
        else {
            const startPage = Math.min(pageNumber, numPages - (maxDisplayPages - 1));
            const endPage = Math.min(pageNumber + (maxDisplayPages - 1), numPages);
            for (let i = startPage; i <= endPage; i++) {
                options.push(i);
            }
            if (pageNumber < numPages - (maxDisplayPages - 1)) {
                options[2] = 0;
                options[3] = numPages;
            }
            else if (pageNumber >= numPages - 1) {
                options[0] = 1;
                options[1] = 0;
            }
        }

        return next(options);

    })

    // build the current page label - ex: "1-10 of 60"
    .then("pageLabel", (_, next:Link<JSX.Element, string>) => next(
        ((pageNumber-1)*itemsPerPage+1) +
        "-" +
        Math.min(itemsPerPage * pageNumber, numItems) +
        " of " +
        numItems
    ))

    // render the menu
    .then(({itemsPerPageOptions, pageOptions, pageLabel}) => (
        <div className="paginator-9pw paginator">
            <div className="num-items">
                <Select
                    list={itemsPerPageOptions}
                    isSelected={v => v === itemsPerPage}
                    onChange={(v) => { onPaginateNumResults(v); onPaginatePageNumber(1); }}
                    captureOutsideClicks={true}
                    xdQaName="ccgallery pagination items-per-page dropdown"
                />
            </div>
            <div className="pages">
                <div className="page-navigator">
                    <button key="pager-back"
                        className={cx("page", "back")}
                        disabled={pageNumber <= 1}
                        onClick={() => onPaginatePageNumber(pageNumber - 1)}
                    ><i className="fa fa-chevron-left"/></button>
                    {pageOptions.map(page => (
                        <button key={"pager-"+page}
                            className={cx("page", {active: page === pageNumber})}
                            disabled={page === 0 || page === pageNumber}
                            onClick={() => onPaginatePageNumber(page)}
                        >{page ? page : "-"}</button>
                    ))}
                    <button key="pager-next"
                        className={cx("page", "next")}
                        disabled={pageNumber >= pageOptions[pageOptions.length - 1]}
                        onClick={() => onPaginatePageNumber(pageNumber + 1)}
                    ><i className="fa fa-chevron-right"/></button>
                </div>
            </div>
            <div className="info">
                {pageLabel}
            </div>
        </div>
    ))

    .run()

);
